import { ReactComponent as CallHistoryIcon } from 'Assets/icons/dotpoints.svg';
import { useViewCallHistory } from 'Components/CallLogs/CallsDirectoryList/hooks/useViewCallHistory';
import { COMMUNICATOR_HIDE_CHAT_CALL_HISTORY } from 'Constants/featureFlags';
import useFeatureFlags from 'Hooks/featureFlags/useFeatureFlags';
import { observer } from 'mobx-react';
import { ContactModelV2 } from 'Models/ContactModelV2';
import React, { useMemo } from 'react';
import {
  ANALYTICS_EVENT_CONVERSATION_DETAILS_CALL_HISTORY_OPTION_VIEWED,
  ANALYTICS_EVENT_CONVERSATION_DETAILS_CALL_HISTORY_SELECTED,
  pushToGTMDataLayer,
} from 'Utils/analytics';
import { Button } from '~/lib/ui/button';
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuItem,
} from '~/lib/ui/sidebar';
import { ContactDuplicateGroup } from '../../../ContactDuplicateGroup';
import { EntityContactCard } from '../../../EntityContactCard';
import { useEntityInfo } from '../../hooks/useEntityInfo';
import { EntityProfileContactDetailsProps } from './index.types';

export const EntityProfileContactDetails = observer(
  ({
    unsavedContact,
    contacts,
    person,
    openContactDetails,
  }: EntityProfileContactDetailsProps) => {
    const viewCallHistory = useViewCallHistory();
    const {
      name,
      phoneNumber,
      image,
      category,
      conversationDestination,
      presence,
    } = useEntityInfo({
      unsavedContact,
      contacts,
      person,
    });

    const { [COMMUNICATOR_HIDE_CHAT_CALL_HISTORY]: hideChatCallHistory } =
      useFeatureFlags([COMMUNICATOR_HIDE_CHAT_CALL_HISTORY]);

    /**
     * In this useMemo we get the contact related to the conversation in order to handle navigation to it.
     * We also obtain here the duplicated contacts to display on the list.
     */
    const { contact, duplicateContacts } = useMemo<{
      contact?: ContactModelV2;
      duplicateContacts?: ContactModelV2[];
    }>(() => {
      if (person) {
        return { contact: person };
      }

      if (contacts) {
        return { contact: contacts[0], duplicateContacts: contacts.slice(1) };
      }

      return {};
    }, [contacts, person]);

    React.useEffect(() => {
      if (!hideChatCallHistory) {
        return;
      }

      pushToGTMDataLayer(
        ANALYTICS_EVENT_CONVERSATION_DETAILS_CALL_HISTORY_OPTION_VIEWED
      );
    }, [hideChatCallHistory]);

    const handleViewCallHistory = () => {
      pushToGTMDataLayer(
        ANALYTICS_EVENT_CONVERSATION_DETAILS_CALL_HISTORY_SELECTED
      );
      viewCallHistory({
        contact,
        phoneNumber: unsavedContact?.phoneNumber || phoneNumber,
      });
    };

    return (
      <SidebarGroup className="px-[7px]">
        <SidebarGroupLabel>Contact details</SidebarGroupLabel>
        <SidebarMenu className="px-0">
          <SidebarMenuItem className="px-0">
            <EntityContactCard
              hasContact={!unsavedContact}
              phoneNumber={phoneNumber}
              image={contact?.avatarUrl || image}
              name={contact?.name || name}
              subTitle={category}
              presence={presence}
              onNavigateToContact={() =>
                (contact || unsavedContact) &&
                openContactDetails(contact || unsavedContact)
              }
              isInactive={contact?.deleted}
            />
            {hideChatCallHistory && (
              <Button
                variant="ghost"
                className="w-full justify-start gap-[5.25px] p-[5.25px]! font-normal mb-4 mt-1"
                aria-label={`View call history for ${name}`}
                onClick={handleViewCallHistory}
              >
                <CallHistoryIcon aria-hidden className="size-[21px]" />
                View call history
              </Button>
            )}
            {duplicateContacts?.length > 0 && (
              <ContactDuplicateGroup
                phoneNumber={conversationDestination}
                contacts={duplicateContacts}
                onNavigateToContact={(contact) => {
                  openContactDetails(contact);
                  pushToGTMDataLayer('conversation_detail_contact_duplicate');
                }}
              />
            )}
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarGroup>
    );
  }
);
